import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, Subscription, of } from 'rxjs';
import { AssistantStep, ClientDiscussion } from './kng-model.assistant';

export interface AgentRun {
  id: string;
  messages: ClientDiscussion['messages'];
  content: string;
  steps: AssistantStep[];
  status: 'running' | 'end' | 'error';
  closer?: { class: string; title: string };
}

/** Id de fil : 8 hex (mint courant) ou UUID legacy. Ignore `resp_…`, state vide. */
export function isDiscussionStateId(state: unknown): state is string {
  return typeof state === 'string'
    && /^[0-9a-f]{8}(-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})?$/i.test(state);
}

/** Mint HTTP en vol : l'URL est encore le fil quitté, `pendingId` n'est pas encore l'id créé. */
export const MINT_IN_FLIGHT = '__mint__';

/** Route encore sur le fil quitté alors que le mint a déjà appliqué `pendingId`. */
export function isStaleDiscussionRoute(
  urlId: string | null,
  pendingId: string | null,
  leftId: string | null,
): boolean {
  return Boolean(pendingId && leftId && urlId === leftId && urlId !== pendingId);
}

export function parseSteps(message: string): AssistantStep[] {
  if (!message) return [];
  const jsonraw = message.match(/<step>([\s\S]*?)<\/step>/gm)?.map(step => step.replace(/<\/?step>/g, '')) || [];
  return jsonraw.map(step => {
    try {
      const json = JSON.parse(step);
      const description = typeof json.description === 'string' ? json.description : '';
      return { ...json, description: description.trim() ? description : '' };
    } catch (e) {
      return { description: step };
    }
  });
}

export function cleanAssistantConsoleText(text: string): string {
  return text
    .replace(/<thinking>[\s\S]*?<\/thinking>/g, '')
    .replace(/<step>[\s\S]*?<\/step>/g, '')
    .replace(/<patch>[\s\S]*?<\/patch>/g, '')
    .replace(/<memories>[\s\S]*?<\/memories>/g, '');
}

function deduplicateSteps(steps: AssistantStep[]): AssistantStep[] {
  const countMap = new Map<string, number>();
  const result: AssistantStep[] = [];

  for (const step of steps) {
    const baseDesc = step.description.replace(/\s*\(\d+\)$/, '');
    const count = (countMap.get(baseDesc) || 0) + 1;
    countMap.set(baseDesc, count);
    const existingIndex = result.findIndex(s =>
      s.description.replace(/\s*\(\d+\)$/, '') === baseDesc
    );
    const newStep = {
      ...step,
      description: count > 1 ? `${baseDesc} (${count})` : baseDesc
    };
    if (existingIndex >= 0) {
      result[existingIndex] = newStep;
    } else {
      result.push(newStep);
    }
  }

  return result;
}

/**
 * Run HTTP : SSE nu + tours POST /api/agent.
 * Attach / detach = le fil lit ou non ce run. Unsubscribe UI ≠ cancel HTTP.
 */
@Injectable({
  providedIn: 'root'
})
export class KngDiscussionStreamService {
  private readonly runs = new Map<string, { run: AgentRun; sub: Subscription }>();
  private attachedId: string | null = null;
  private readonly updatesSubject = new Subject<AgentRun>();
  private readonly runningIdsSubject = new BehaviorSubject<string[]>([]);

  readonly updates$ = this.updatesSubject.asObservable();
  readonly runningIds$ = this.runningIdsSubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  alive(id?: string | null): boolean {
    return Boolean(id && this.runs.get(id)?.run.status === 'running');
  }

  get(id: string | null | undefined): AgentRun | undefined {
    return id ? this.runs.get(id)?.run : undefined;
  }

  attach(id: string): void {
    this.attachedId = id;
    const run = this.runs.get(id)?.run;
    if (run) {
      this.updatesSubject.next(cloneRun(run));
    }
  }

  detach(): void {
    this.attachedId = null;
  }

  sse(url: string, body: any, options: { raw?: boolean } = {}): Observable<string> {
    return new Observable<string>(observer => {
      const headers = new HttpHeaders()
        .set('Accept', 'text/event-stream')
        .set('Cache-Control', 'no-cache')
        .set('ngsw-bypass', 'true');
      const request = new HttpRequest('POST', url, body, {
        reportProgress: true,
        responseType: 'text',
        headers,
      });
      let lastIndex = 0;
      const subscription = this.http.request(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const partialText = (event as any).partialText || '';
            if (!partialText) {
              return;
            }
            const chunk = partialText.substring(lastIndex);
            lastIndex = partialText.length;
            observer.next(options.raw ? chunk : cleanAssistantConsoleText(chunk));
          } else if (event.type === HttpEventType.Response) {
            const body = typeof event.body === 'string' ? event.body : '';
            if (body.length > lastIndex) {
              const chunk = body.substring(lastIndex);
              lastIndex = body.length;
              observer.next(options.raw ? chunk : cleanAssistantConsoleText(chunk));
            }
            observer.complete();
          }
        },
        error: (err) => observer.error(err),
      });
      return () => subscription.unsubscribe();
    });
  }

  start(
    id: string,
    url: string,
    body: any,
    messages: ClientDiscussion['messages'] = [],
  ): Observable<string> {
    if (!id || this.alive(id)) {
      return of('');
    }
    const existing = this.runs.get(id);
    if (existing) {
      existing.sub.unsubscribe();
      this.runs.delete(id);
    }
    const run: AgentRun = {
      id,
      messages: cloneMessages(messages),
      content: '',
      steps: [],
      status: 'running',
    };
    this.attachedId = id;
    const chunks = new Subject<string>();
    let contentAccum = '';
    let stepsAccum: AssistantStep[] = [];
    const sub = this.sse(url, body, { raw: true }).subscribe({
      next: (chunk) => {
        const chunkSteps = parseSteps(chunk);
        const closer = chunkSteps.find(step => step.data?.closer);
        if (closer?.data?.closer) {
          run.closer = closer.data.closer;
        }
        const visible = chunkSteps.filter(step => !step.data?.closer && String(step.description || '').trim());
        if (visible.length) {
          stepsAccum = deduplicateSteps([...stepsAccum, ...visible]);
          run.steps = stepsAccum;
        }
        const cleaned = cleanAssistantConsoleText(chunk);
        if (cleaned.trim()) {
          contentAccum += cleaned;
          run.content = contentAccum;
        }
        if (cleaned.trim() || visible.length) {
          patchAssistant(run, run.content, run.steps);
        }
        this.emitIfAttached(run);
        chunks.next(cleaned);
      },
      error: (err) => {
        run.status = 'error';
        run.messages = finalizeCompletedAssistants(run.messages);
        this.emitIfAttached(run);
        this.publishRunning();
        chunks.error(err);
      },
      complete: () => {
        run.status = 'end';
        run.messages = finalizeCompletedAssistants(run.messages);
        this.emitIfAttached(run);
        this.publishRunning();
        chunks.complete();
      },
    });
    this.runs.set(id, { run, sub });
    this.publishRunning();
    this.emitIfAttached(run);
    return chunks.asObservable();
  }

  private emitIfAttached(run: AgentRun): void {
    if (this.attachedId === run.id) {
      this.updatesSubject.next(cloneRun(run));
    }
  }

  private publishRunning(): void {
    const ids = [...this.runs.entries()]
      .filter(([, entry]) => entry.run.status === 'running')
      .map(([id]) => id);
    this.runningIdsSubject.next(ids);
  }
}

function cloneMessages(messages: ClientDiscussion['messages']): ClientDiscussion['messages'] {
  return messages.map(message => ({
    ...message,
    ...(message.steps ? { steps: [...message.steps] } : {}),
  }));
}

function cloneRun(run: AgentRun): AgentRun {
  return {
    ...run,
    messages: cloneMessages(run.messages),
    steps: [...run.steps],
  };
}

function finalizeCompletedAssistants(
  messages: ClientDiscussion['messages'],
): ClientDiscussion['messages'] {
  return messages.map(message => {
    if (message.role !== 'assistant' || !String(message.id).startsWith('temp-assistant-')) {
      return message;
    }
    return {
      ...message,
      id: String(message.id).replace(/^temp-assistant-/, 'assistant-'),
      steps: undefined,
    };
  });
}

function patchAssistant(
  run: Pick<AgentRun, 'messages'>,
  content: string,
  steps?: AssistantStep[],
): void {
  let index = -1;
  for (let cursor = run.messages.length - 1; cursor >= 0; cursor -= 1) {
    const message = run.messages[cursor];
    if (message.role === 'assistant' && String(message.id).startsWith('temp-assistant-')) {
      index = cursor;
      break;
    }
  }
  if (index < 0) {
    run.messages = [
      ...run.messages,
      {
        id: `temp-assistant-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        steps: steps ? [...steps] : [],
      },
    ];
    return;
  }
  run.messages[index] = {
    ...run.messages[index],
    content,
    ...(steps ? { steps: [...steps] } : {}),
  };
}
